import { UploadOutlined } from '@ant-design/icons';
import { Button, Checkbox, Input, message, Upload, UploadFile } from 'antd';

import { InterviewScriptTemplateCollectionInputSerializer } from '../types/api';
import Form from './Form';
import RichEditor from './RichEditor';

export interface InterviewScriptTemplateFormValues {
  name: string;
  type: InterviewScriptTemplateCollectionInputSerializer['type'];
  content: string;
  emailCommunicationContent: string;
  attachments: UploadFile[];
}

const mimeTypes = {
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  doc: 'application/msword',
  pdf: 'application/pdf',
  jpg: 'image/jpeg',
  png: 'image/png',
  jpeg: 'image/jpeg',
  webp: 'image/webp',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  xls: 'application/vnd.ms-excel',
  csv: 'text/csv',
};

export function fileTypeToMimeType(extension: string): string | undefined {
  return mimeTypes[extension as keyof typeof mimeTypes];
}

export const supportedFiles = [
  '.doc',
  '.docx',
  '.pdf',
  '.jpg',
  '.png',
  '.jpeg',
  '.webp',
  '.csv',
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/msword',
  'image/png',
  'image/jpeg',
  'image/webp',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'text/csv',
];

export const interviewScriptTemplateTypeOptions = [
  {
    key: 'HR_INTERVIEW',
    value: 'HR_INTERVIEW',
    label: 'Entrevista com RH',
  },
  {
    key: 'MANAGER_INTERVIEW',
    value: 'MANAGER_INTERVIEW',
    label: 'Entrevista com gestor(a)',
  },
  {
    key: 'STAKEHOLDERS_INTERVIEW',
    value: 'STAKEHOLDERS_INTERVIEW',
    label: 'Entrevista com demais envolvidos',
  },
  {
    key: 'PRACTICAL_TECHNICAL_CASE',
    value: 'PRACTICAL_TECHNICAL_CASE',
    label: 'Case prático ou técnico',
  },
];

const InterviewScriptTemplateForm = () => {
  const form = Form.useFormInstance();
  const typeValue = Form.useWatch<InterviewScriptTemplateFormValues['type'] | undefined>(
    'type',
    form
  );

  const options = interviewScriptTemplateTypeOptions.map(({ key, value, label }) => {
    if (typeValue?.includes('PRACTICAL_TECHNICAL_CASE')) {
      return {
        key,
        label,
        value,
        disabled: value !== 'PRACTICAL_TECHNICAL_CASE',
      };
    }

    return {
      key,
      label,
      value,
    };
  });

  return (
    <>
      <Form.Item name="name" label="Nome do roteiro" rules={[{ required: true }]}>
        <Input />
      </Form.Item>
      <Form.Item name="type" label="Tipo de entrevista" rules={[{ required: true }]}>
        <Checkbox.Group
          options={options}
          onChange={(checkedValue) => {
            if (checkedValue.includes('PRACTICAL_TECHNICAL_CASE') && checkedValue.length > 1) {
              form?.setFieldValue('type', ['PRACTICAL_TECHNICAL_CASE']);
            }
          }}
        />
      </Form.Item>
      <Form.Item
        name="content"
        label={
          typeValue?.includes('PRACTICAL_TECHNICAL_CASE')
            ? 'Instruções para o avaliador'
            : 'Roteiro'
        }
        rules={[{ required: true }]}
      >
        <RichEditor image={false} video={false} />
      </Form.Item>
      {(typeValue || []).includes('PRACTICAL_TECHNICAL_CASE') && (
        <>
          <Form.Item
            name="emailCommunicationContent"
            label="Descrição para o candidato"
            rules={[{ required: true }]}
          >
            <RichEditor image={false} video={false} />
          </Form.Item>
          <Form.Item
            name="attachments"
            valuePropName="fileList"
            getValueFromEvent={(e) => {
              if (Array.isArray(e)) {
                return e;
              }
              return e && e.fileList;
            }}
          >
            <Upload
              data-testid="upload-attachments"
              listType="text"
              accept={supportedFiles.join(',')}
              beforeUpload={(file: UploadFile) => {
                if (fileTypeToMimeType(file.name.split('.').pop()?.toLowerCase() || '')) {
                  return false;
                } else {
                  message.error('Por favor, selecione um arquivo que é aceito pelo sistema.');
                  return Upload.LIST_IGNORE;
                }
              }}
            >
              <Button icon={<UploadOutlined />}>Upload</Button>
            </Upload>
          </Form.Item>
        </>
      )}
    </>
  );
};

export default InterviewScriptTemplateForm;
